import ExtensionService from "@/service/ExtensionService";
import { getDefaultFoodImage } from "./lunchFoodAssets";

const MAX_SOURCE_SIZE = 2 * 1024 * 1024;
const MAX_OUTPUT_SIZE = 100 * 1024;
const MAX_DIMENSION = 300;
const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const FOOD_IMAGE_DIMENSION_ERROR = "FOOD_IMAGE_DIMENSION_EXCEEDED";

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Không đọc được ảnh đã chọn."));
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.type !== "image/webp") {
        reject(new Error("Trình duyệt không hỗ trợ chuyển ảnh sang WebP."));
        return;
      }
      resolve(blob);
    }, "image/webp", quality);
  });
}

export function getFoodImageUrl(image, category) {
  if (!image) return getDefaultFoodImage(category);
  const value = String(image);
  if (!/^[a-f\d]{24}$/i.test(value)) return value;
  return `${ExtensionService.urlImg}${value}&r=square&w=${MAX_DIMENSION}`;
}

export async function prepareFoodImage(file) {
  if (!file || !SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Ảnh phải có định dạng JPG, PNG hoặc WebP.");
  }
  if (file.size > MAX_SOURCE_SIZE) {
    throw new Error("Ảnh tải lên không được vượt quá 2 MB.");
  }

  const image = await loadImage(file);
  if (!image.naturalWidth || !image.naturalHeight) {
    throw new Error("Kích thước ảnh không hợp lệ.");
  }
  if (image.naturalWidth > MAX_DIMENSION || image.naturalHeight > MAX_DIMENSION) {
    const error = new Error(
      `Kích thước ảnh không được vượt quá ${MAX_DIMENSION} x ${MAX_DIMENSION}px.`
    );
    error.code = FOOD_IMAGE_DIMENSION_ERROR;
    throw error;
  }

  const scale = Math.min(
    1,
    MAX_DIMENSION / image.naturalWidth,
    MAX_DIMENSION / image.naturalHeight
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Không thể xử lý ảnh đã chọn.");
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let blob;
  for (const quality of [0.8, 0.7, 0.6, 0.5]) {
    blob = await canvasToBlob(canvas, quality);
    if (blob.size <= MAX_OUTPUT_SIZE) break;
  }
  if (!blob || blob.size > MAX_OUTPUT_SIZE) {
    throw new Error("Ảnh sau khi xử lý vẫn vượt quá 100 KB. Vui lòng chọn ảnh khác.");
  }

  const originalName = file.name || "food-image";
  const nameWithoutExtension = originalName.replace(/\.[^.]+$/, "") || "food-image";
  return {
    raw: blob,
    name: `${nameWithoutExtension}.webp`
  };
}
